import type { CSSProperties } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import type { SxProps, Theme } from '@mui/material';
import { ButtonBase, styled } from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';

import { HStack } from '../Stack';

export interface LookoTooltipColorBaseProps {
  /**
   * 툴팁 형태
   *
   * @default primary
   */
  variant?: 'primary' | 'secondary' | 'highlight';

  /**
   * 값이 주어지면 컨트롤 모드로 동작합니다.
   */
  open?: boolean;

  /**
   * 툴팁 텍스트.
   */
  title: React.ReactNode;

  /**
   * 툴팁 위치.
   *
   * @default bottom
   */
  placement?: TooltipProps['placement'];

  /**
   * 툴팁 최대 너비(MUI 기본값 300px)
   */
  maxWidth?: CSSProperties['maxWidth'];

  /**
   * 다른 뷰의 상단으로 올라오지 않도록 합니다. 즉, z-index 를 0으로 바꿉니다.
   */
  enablePopover?: boolean;
}

export type LookoTooltipColorProps = LookoTooltipColorBaseProps &
  (
    | {
        hasClose?: false;
      }
    | {
        hasClose: true;
        onClose: () => void;
      }
  );

const variantToColor: Record<
  NonNullable<LookoTooltipColorProps['variant']>,
  {
    backgroundColor: CSSProperties['color'];
    color: CSSProperties['color'];
  }
> = {
  primary: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  secondary: {
    backgroundColor: '#000',
    color: '#FFF',
  },
  highlight: {
    backgroundColor: '#EF5350',
    color: '#FFF',
  },
};

const closeIconStyle: SxProps<Theme> = { fontSize: 16 };

/**
 * 색상 툴팁. `variant`로 스타일 변경.
 *
 * 비제어 모드 (hover로 자동 제어):
 * ```tsx
 * <LookoTooltipColor title="툴팁 텍스트">
 *   <Button>버튼</Button>
 * </LookoTooltipColor>
 * ```
 *
 * 제어 모드 (`open` prop 직접 제어):
 * ```tsx
 * <LookoTooltipColor title="툴팁 텍스트" open={isOpen}>
 *   <Button>버튼</Button>
 * </LookoTooltipColor>
 * ```
 */
export const LookoTooltipColor = styled(
  ({
    className,
    maxWidth,
    enablePopover,
    ...props
  }: Pick<TooltipProps, 'className' | 'children'> & LookoTooltipColorProps) => {
    if (props.hasClose) {
      const { onClose, hasClose, ...restProps } = props;

      return (
        <Tooltip
          {...restProps}
          title={
            <HStack spacing={1} alignItems={'center'}>
              <span>{props.title}</span>
              <ButtonBase onClick={onClose}>
                <CloseIcon sx={closeIconStyle} />
              </ButtonBase>
            </HStack>
          }
          arrow
          classes={{ popper: className }}
        >
          {props.children}
        </Tooltip>
      );
    } else {
      return <Tooltip {...props} arrow classes={{ popper: className }} />;
    }
  },
)(({ theme, variant = 'primary', enablePopover, ...props }) => {
  return {
    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.3))',
    [`& .${tooltipClasses.arrow}`]: {
      color: variantToColor[variant].backgroundColor,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      ...theme.typography.Subtitle_Looko,
      maxWidth: props.maxWidth,
      ...variantToColor[variant],
      padding: '8px 16px',
    },
    ...(!enablePopover && {
      zIndex: 0,
    }),
  };
});
